import { valibotResolver } from "@hookform/resolvers/valibot";
import { LinkedinLogo } from "@phosphor-icons/react";
import clsx from "clsx";
import { useState } from "react";
import { useForm } from "react-hook-form";

import MaxLengthInput from "common/components/MaxLengthInput";
import { Button } from "common/components/ui/Button";
import { DialogTitle } from "common/components/ui/Dialog/Dialog";
import { DrawerTitle } from "common/components/ui/Drawer/Drawer";
import FormField from "common/components/ui/FormField";
import WorkspaceAccountSelector from "common/components/WorkspaceAccountSelector";
import { useSelectedWorkspaceContext } from "common/helpers/SelectedWorkspaceContext";
import { getSchemaMaxLength } from "common/helpers/utils";
import useTwBreakpoint from "common/hooks/useTwBreakpoint";
import useWorkspaceNavigate from "common/hooks/useWorkspaceNavigate";
import { CampaignNameSchema } from "common/schemas";
import type { CampaignType } from "common/types";

import useCampaignMutations from "../../datahooks/useCampaignMutations";

interface DuplicateCampaignProps {
  campaignId: string;
  type: CampaignType;
  name: string;
  accountIds: string[];
  isDrip: boolean;
  onCancel: () => void;
}

export default function DuplicateCampaign({
  campaignId,
  name,
  accountIds,
  type,
  isDrip,
  onCancel,
}: DuplicateCampaignProps) {
  const isDesktop = useTwBreakpoint("lg");
  const [selectedAccountIds, setSelectedAccountIds] = useState(accountIds);

  const { duplicateCampaign, isDuplicatingCampaign } = useCampaignMutations();
  const { relativeNavigate } = useWorkspaceNavigate();

  const { accounts } = useSelectedWorkspaceContext();

  const navigatorAccounts = accounts.filter(
    ({ sales_navigator, state }) => state === "LoggedIn" && sales_navigator,
  );

  const isList = type === "list" || type === "crm";

  const workerAccounts = selectedAccountIds.length
    ? selectedAccountIds
    : accounts.map(({ id }) => id);

  const selection = isDrip
    ? (selectedAccountIds[0] ?? accounts[0].id)
    : selectedAccountIds;

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm({
    defaultValues: {
      name: `[Copy] ${name}`,
    },
    resolver: valibotResolver(CampaignNameSchema),
  });

  function onDuplicate(data: { name: string }) {
    duplicateCampaign({
      campaignId,
      name: data.name,
      accountIds: workerAccounts,
      ...(isList
        ? {}
        : {
            miner:
              type === "navigator"
                ? navigatorAccounts?.[0].id
                : workerAccounts[0],
          }),
    }).then((campaign) => {
      relativeNavigate(`campaigns/${campaign.id}`);
    });
  }

  const Title = isDesktop ? DialogTitle : DrawerTitle;

  return (
    <>
      <Title className="mb-8 lg:mb-10">
        Which account will run this campaign?
      </Title>

      <FormField
        label="Campaign name"
        htmlFor="campaignName"
        className="mb-8"
        error={errors.name?.message}
        size="lg"
      >
        <MaxLengthInput
          id="campaignName"
          variant="lg"
          characterLength={watch("name").length}
          maxLength={getSchemaMaxLength(CampaignNameSchema.entries.name)}
          placeholder="Campaign name"
          {...register("name")}
        />
      </FormField>

      <section className="flex flex-col justify-between gap-3 lg:flex-row lg:items-center">
        <div className="flex gap-x-2.5">
          <span
            className={clsx(
              "inline-flex size-10 shrink-0 items-center justify-center rounded-2xl",
              isDrip ? "bg-blue-100 text-blue-600" : "bg-black-100",
            )}
          >
            <LinkedinLogo size={20} />
          </span>

          <div className="flex flex-col gap-0.5">
            <h3 className="text-button-md">
              LinkedIn Account{isDrip ? "" : "s"}
            </h3>

            <p className="text-xs text-black-500">
              What LinkedIn account will run this campaign
            </p>
          </div>
        </div>

        <WorkspaceAccountSelector
          selection={selection}
          setSelection={(nextSelection) =>
            setSelectedAccountIds(
              Array.isArray(nextSelection) ? nextSelection : [nextSelection],
            )
          }
          className="w-48 max-lg:ml-[50px]"
        />
      </section>

      <div className="mt-8 flex justify-between gap-x-2.5">
        <Button
          variant="secondary-black"
          className="lg:flex-1"
          onClick={onCancel}
          disabled={isDuplicatingCampaign}
        >
          Cancel
        </Button>

        <Button
          className="lg:flex-1"
          disabled={type === "navigator" && !navigatorAccounts.length}
          isLoading={isDuplicatingCampaign}
          onClick={handleSubmit(onDuplicate)}
        >
          Duplicate
        </Button>
      </div>
    </>
  );
}
